import {
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import {
  getCertificationTypesRequest,
  getInspectionServicesRequest,
  getInsuranceTypesRequest,
} from "../../../../../../redux/inspection-firms/slice";
import DialogContainer from "../../../../../../components/dialog-container/dialog-container";
import TextInput from "../../../../../../components/textinput/textinput";
import ErrorNotification from "../../../../../../components/error-notification/error-notification";
import Loading from "../../../../../../components/loading/loading";
import {
  formTemplateRestrictionOptions,
  photoResolutions,
} from "../../../../../../utils/Constant";
import {
  getFormTemplateDetailsRequest,
  getFormTemplateDetailsSuccess,
  getFormTemplateListRequest,
} from "../../../../../../redux/form-templates/slice";
import {
  getFormsRequest,
  getProcessesProcedureRequest,
  getProcessesProcedureSuccess,
} from "../../../../../../redux/clients/slice";
import { addFormRequest } from "../../../../../../utils/APICalls";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Attachments from "./attachments";

type props = {
  showDialog: boolean;
  closeDialog: any;
  clientId: string;
};

type FormValues = {
  formTemplateId: string;
  formName: string;
  category: string;
  type: string;
  availableOutputs: { excel: boolean; pdf: boolean };
  clientFee: string;
  inspectorFee: string;
  photoResolution: string;
  secondQC: boolean;
  attachments: any[];
};

const ValidationSchema = yup.object({
  formTemplateId: yup.string().required("This field can't be empty"),
  formName: yup.string().required("This field can't be empty"),
  category: yup.string().when("formTemplateId", {
    is: (value: string) => value !== "customForm",
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required("This field can't be empty"),
  }),
  type: yup.string().when("formTemplateId", {
    is: (value: string) => value !== "customForm",
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required("This field can't be empty"),
  }),
  availableOutputs: yup
    .object({
      pdf: yup.boolean(),
      excel: yup.boolean(),
    })
    .test(
      "at-least-one-true",
      "Select at least one output (Excel or PDF)",
      (value) => {
        return value && (value.excel || value.pdf);
      }
    ),
  clientFee: yup
    .number()
    .required("This field can't be empty")
    .positive("Amount must be greater than 0"),
  inspectorFee: yup
    .number()
    .required("This field can't be empty")
    .positive("Amount must be greater than 0"),
  attachments: yup.array(),
});

export default function AddForm({ showDialog, closeDialog, clientId }: props) {
  const [category, setCategory] = useState<any[]>([]);
  const [type, setType] = useState<any[]>([]);
  const [restrictions, setRestrictions] = useState<any[]>([]);
  const [backgroundCheck, setBackgroundCheck] = useState("true");
  const [insurances, setInsurances] = useState<string[]>([]);
  const [certifications, setCertifications] = useState<string[]>([]);
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const dispatch = useDispatch();

  const { inspectionServices, insuranceTypes, certificationTypes, loading } =
    useSelector((state: any) => state.inspectionFirms);
  const {
    loading: formLoading,
    formTemplateList,
    formTemplateDetails,
  } = useSelector((state: any) => state.formTemplates);
  const { processesProcedure } = useSelector((state: any) => state?.clients);

  useEffect(() => {
    dispatch(getFormTemplateDetailsSuccess({ response: null }));
    dispatch(getProcessesProcedureSuccess({ response: null }));

    if (inspectionServices.length === 0)
      dispatch(getInspectionServicesRequest());
    if (insuranceTypes.length === 0) dispatch(getInsuranceTypesRequest());
    if (certificationTypes.length === 0)
      dispatch(getCertificationTypesRequest());

    dispatch(getFormTemplateListRequest());
    dispatch(getProcessesProcedureRequest({ id: clientId }));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (inspectionServices && inspectionServices.length > 0) {
      const uniqueCategories = inspectionServices.reduce(
        (acc: any[], item: any) => {
          if (!acc.includes(item.category)) {
            acc.push(item.category);
          }
          return acc;
        },
        []
      ).sort();
      setCategory(uniqueCategories);
    }
  }, [inspectionServices]);

  const formik = useFormik<FormValues>({
    // enableReinitialize: false,
    initialValues: {
      formTemplateId: "",
      formName: "",
      category: "",
      type: "",
      availableOutputs: {
        excel: false,
        pdf: false,
      },
      clientFee: "",
      inspectorFee: "",
      photoResolution: photoResolutions.standardResolution,
      secondQC: false,
      attachments: [],
    },
    validationSchema: ValidationSchema,
    onSubmit: (values) => {
      if (isRestricionTypeEmpty()) {
        return;
      }
      const backgroundCheckFilter = restrictions.filter(
        (restriction) => restriction.type === "Background Check Completed"
      );
      const certificationFilter = restrictions.filter(
        (restriction) => restriction.type === "Certifications"
      );
      const insuranceFilter = restrictions.filter(
        (restriction) => restriction.type === "Insurance Required"
      );
      if (
        insuranceFilter &&
        insuranceFilter.length > 0 &&
        insurances.length === 0
      ) {
        return;
      }
      if (
        certificationFilter &&
        certificationFilter.length > 0 &&
        certifications.length === 0
      ) {
        return;
      }

      const availableOutputs: string[] = [];
      if (values.availableOutputs.excel) availableOutputs.push("Excel");
      if (values.availableOutputs.pdf) availableOutputs.push("PDF");

      if (availableOutputs.length === 0) {
        return;
      }

      const restrictionsPayload: any = {};
      if (backgroundCheckFilter.length > 0) {
        restrictionsPayload.backgroundCheckDone =
          backgroundCheck === "true" ? true : false;
      }
      if (insuranceFilter.length > 0) {
        restrictionsPayload.insurances = insurances;
      }
      if (certificationFilter.length > 0) {
        restrictionsPayload.certifications = certifications;
      }

      let attachmentKeys = [];
      if (values.attachments && values.attachments.length > 0) {
        attachmentKeys = values.attachments.map((att) => att.attachmentKey);
      }

      const payload: any = {
        clientId: clientId,
        displayName: values.formName,
        inspectionType: values.type,
        clientFee: values.clientFee,
        inspectorFee: values.inspectorFee,
        availableOutputs: availableOutputs,
        photoResolution: values.photoResolution,
        requireSecondQc: values.secondQC,
        attachmentKeys: attachmentKeys,
      };
      if (values.formTemplateId !== "customForm") {
        payload.formTemplateId = values.formTemplateId;
      }
      if (Object.keys(restrictionsPayload).length > 0) {
        payload.restrictions = restrictionsPayload;
      }
      addForm(payload);
    },
  });

  const isRestricionTypeEmpty = () => {
    let isEmpty = false;
    restrictions.forEach((restriction) => {
      if (!restriction.type) {
        isEmpty = true;
      }
    });
    return isEmpty;
  };

  const onCategoryChange = (event: SelectChangeEvent) => {
    formik.handleChange(event);
    formik.setFieldValue("type", null);
    formik.setFieldTouched("category", false);
    const value = event.target.value;
    const filterTypes = inspectionServices.filter(
      (service: any) => service.category === value
    );
    setType(filterTypes);
  };

  const onRestrictionTypeChange = (e: SelectChangeEvent, index: number) => {
    const allRestrictions = [...restrictions];
    allRestrictions[index].type = e.target.value;
    setRestrictions(allRestrictions);
  };

  const onBackgroundCheckChange = (e: SelectChangeEvent) => {
    setBackgroundCheck(e.target.value);
  };

  const onInsuranceChange = (event: SelectChangeEvent<typeof insurances>) => {
    const { value }: any = event.target;
    setInsurances(value);
  };

  const onInsuranceDelete = (option: string) => {
    setInsurances((prev) => prev.filter((value) => value !== option));
  };

  const onCertificationChange = (
    event: SelectChangeEvent<typeof certifications>
  ) => {
    const { value }: any = event.target;
    setCertifications(value);
  };

  const onCertificationDelete = (option: string) => {
    setCertifications((prev) => prev.filter((value) => value !== option));
  };

  const addRestictrionClick = () => {
    setRestrictions((prev) => [...prev, { type: "" }]);
  };

  const checkDuplicate = (option: string) => {
    let isSelected = false;
    restrictions.forEach((restriction) => {
      if (restriction.type === option) {
        isSelected = true;
      }
    });
    return isSelected;
  };

  const deleteRestictrionClick = (index: number) => {
    const allRestrictions = [...restrictions];
    allRestrictions.splice(index, 1);
    setRestrictions(allRestrictions);
  };

  const getInsuranceDisplayName = (insuranceType: string) => {
    const filterUser = insuranceTypes?.filter(
      (insurance: any) => insurance.insuranceType === insuranceType
    );
    return filterUser && filterUser.length > 0 && filterUser[0].displayName;
  };

  const getCertificationDisplayName = (certificationType: string) => {
    const filterUser = certificationTypes?.filter(
      (certification: any) =>
        certification.certificationType === certificationType
    );
    return filterUser && filterUser.length > 0 && filterUser[0].displayName;
  };

  const addForm = async (payload: any) => {
    setShowLoading(true);
    const response = await addFormRequest(payload);
    setShowLoading(false);
    if (response?.status === 201) {
      dispatch(getFormsRequest({ clientId: clientId, page: 1, pageSize: 10 }));
      closeDialog();
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const onTemplateSelect = (e: SelectChangeEvent) => {
    const { value } = e.target;
    if (value === "customForm") {
      // if custom form select, set category & type as null
      formik.setFieldValue("formTemplateId", value);
      formik.setFieldValue("category", null);
      formik.setFieldValue("type", null);
      setType([]);
      formik.setFieldValue("availableOutputs.pdf", false);
      formik.setFieldValue("availableOutputs.excel", false);
      formik.setFieldValue("clientFee", "");
      formik.setFieldValue("inspectorFee", "");
      formik.setFieldValue("secondQC", false);
    } else {
      formik.setFieldValue("formTemplateId", value);
      dispatch(getFormTemplateDetailsRequest({ id: value }));
    }
  };

  useEffect(() => {
    if (formTemplateDetails) {
      const filterTypes = inspectionServices.filter(
        (service: any) =>
          service.category === formTemplateDetails.inspectionTypeCategory
      );
      setType(filterTypes);
      formik.setFieldValue(
        "category",
        formTemplateDetails.inspectionTypeCategory
      );
      formik.setFieldTouched("category", false);
      formik.setFieldValue("type", formTemplateDetails.inspectionType);
      formik.setFieldTouched("type", false);

      if (
        formTemplateDetails.availableOutputs &&
        formTemplateDetails.availableOutputs.length > 0
      ) {
        const isPdf = formTemplateDetails.availableOutputs?.includes("PDF");
        formik.setFieldValue("availableOutputs.pdf", isPdf, true);
        const isExcel = formTemplateDetails.availableOutputs?.includes("Excel");
        formik.setFieldValue("availableOutputs.excel", isExcel, true);
      }

      if (
        processesProcedure?.services &&
        processesProcedure?.services.length > 0
      ) {
        const filterService = processesProcedure.services.filter(
          (service: any) =>
            service?.inspectionType === formTemplateDetails?.inspectionType
        );
        if (filterService.length > 0) {
          //set client fee & inspector fee from processes & procedures services if found
          formik.setFieldValue("clientFee", filterService[0].clientFee);
          formik.setFieldTouched("clientFee", false);
          formik.setFieldValue("inspectorFee", filterService[0].inspectorFee);
          formik.setFieldTouched("inspectorFee", false);
        } else {
          formik.setFieldValue(
            "clientFee",
            formTemplateDetails.clientFee || ""
          );
          formik.setFieldTouched("clientFee", false);
          formik.setFieldValue(
            "inspectorFee",
            formTemplateDetails.inspectorFee || ""
          );
          formik.setFieldTouched("inspectorFee", false);
        }
      } else {
        formik.setFieldValue("clientFee", formTemplateDetails.clientFee || "");
        formik.setFieldTouched("clientFee", false);
        formik.setFieldValue(
          "inspectorFee",
          formTemplateDetails.inspectorFee || ""
        );
        formik.setFieldTouched("inspectorFee", false);
      }

      formik.setFieldValue(
        "secondQC",
        formTemplateDetails.requireSecondQc || false
      );
    } else {
      formik.setFieldValue("formTemplateId", "");
      formik.setFieldValue("category", null);
      formik.setFieldValue("type", null);
      setType([]);
      formik.setFieldValue("availableOutputs.pdf", false);
      formik.setFieldValue("availableOutputs.excel", false);
      formik.setFieldValue("clientFee", "");
      formik.setFieldValue("inspectorFee", "");
      formik.setFieldValue("secondQC", false);
    }
  }, [formTemplateDetails]); // eslint-disable-line

  return (
    <DialogContainer
      title="Add Form"
      showDialog={showDialog}
      closeDialog={closeDialog}
      buttonText={"Add Form"}
      onSubmit={formik.handleSubmit}
    >
      <form onSubmit={formik.handleSubmit}>
        <Box className="user-name-layout">
          <Typography className="label-txt">Template</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <FormControl
              sx={{ width: "100% !important" }}
              error={
                formik.touched.formTemplateId &&
                Boolean(formik.errors.formTemplateId)
              }
            >
              <Select
                id="formTemplateId"
                name="formTemplateId"
                value={formik.values.formTemplateId}
                onChange={onTemplateSelect}
              >
                {formTemplateList?.map((item: any) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.displayName}
                  </MenuItem>
                ))}
                <MenuItem key="customForm" value="customForm">
                  No Template/Custom Form
                </MenuItem>
              </Select>
              {Boolean(formik.errors.formTemplateId) &&
                formik.touched.formTemplateId && (
                  <FormHelperText>
                    {formik.errors.formTemplateId}
                  </FormHelperText>
                )}
            </FormControl>
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Form Name</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <TextInput
              sx={{ width: "100% !important" }}
              className="field-input"
              id="formName"
              name="formName"
              variant="outlined"
              value={formik.values.formName}
              onChange={formik.handleChange}
              error={formik.touched.formName && Boolean(formik.errors.formName)}
              helperText={formik.touched.formName && formik.errors.formName}
            />
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Category</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <FormControl
              sx={{ width: "100% !important" }}
              error={formik.touched.category && Boolean(formik.errors.category)}
            >
              <Select
                id="category"
                name="category"
                value={formik.values.category}
                onChange={onCategoryChange}
                disabled={formik.values.formTemplateId !== "customForm"}
              >
                {category.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
              {Boolean(formik.errors.category) && formik.touched.category && (
                <FormHelperText>{formik.errors.category}</FormHelperText>
              )}
            </FormControl>
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Type</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <FormControl
              sx={{ width: "100% !important" }}
              error={formik.touched.type && Boolean(formik.errors.type)}
              disabled={
                formik.values.formTemplateId !== "customForm" ||
                type.length === 0
              }
            >
              <Select
                id="type"
                name="type"
                value={formik.values.type}
                onChange={formik.handleChange}
              >
                {type.map((item) => (
                  <MenuItem
                    key={item.inspectionType}
                    value={item.inspectionType}
                  >
                    {item.displayName}
                  </MenuItem>
                ))}
              </Select>
              {Boolean(formik.errors.type) && formik.touched.type && (
                <FormHelperText>{formik.errors.type}</FormHelperText>
              )}
            </FormControl>
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Available Output</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    name="availableOutputs.excel"
                    checked={formik.values.availableOutputs.excel}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "availableOutputs.excel",
                        e.target.checked
                      )
                    }
                  />
                }
                label="Excel"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="availableOutputs.pdf"
                    checked={formik.values.availableOutputs.pdf}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "availableOutputs.pdf",
                        e.target.checked
                      )
                    }
                  />
                }
                label="PDF"
              />
              {formik.submitCount > 0 &&
                formik.errors.availableOutputs &&
                typeof formik.errors.availableOutputs === "string" && (
                  <Typography
                    sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                    color="error"
                  >
                    {formik.errors.availableOutputs}
                  </Typography>
                )}
            </FormGroup>
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Attachments</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <Typography sx={{ fontStyle: "italic" }}>
              The following atachments will be included for every order placed
              using this form. They will be visible to inspectors...fpo
            </Typography>
            <Attachments formik={formik} />
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Client Fee</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <FormControl
              fullWidth
              error={
                formik.touched.clientFee && Boolean(formik.errors.clientFee)
              }
            >
              <InputLabel htmlFor="outlined-client-fee-amount"></InputLabel>
              <OutlinedInput
                sx={{
                  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                    {
                      display: "none",
                      margin: 0,
                    },
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                  },
                }}
                id="outlined-client-fee-amount"
                name="clientFee"
                type="number"
                startAdornment={
                  <InputAdornment position="start">
                    <AttachMoneyOutlinedIcon
                      sx={{ color: "rgba(0, 0, 0, 0.87)", fontSize: 20 }}
                    />
                  </InputAdornment>
                }
                value={formik.values.clientFee}
                onChange={formik.handleChange}
              />
              {Boolean(formik.errors.clientFee) && formik.touched.clientFee && (
                <FormHelperText>{formik.errors.clientFee}</FormHelperText>
              )}
            </FormControl>
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Inspector Fee</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <FormControl
              fullWidth
              error={
                formik.touched.inspectorFee &&
                Boolean(formik.errors.inspectorFee)
              }
            >
              <InputLabel htmlFor="outlined-inspector-fee-amount"></InputLabel>
              <OutlinedInput
                sx={{
                  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                    {
                      display: "none",
                      margin: 0,
                    },
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                  },
                }}
                id="outlined-inspector-fee-amount"
                name="inspectorFee"
                type="number"
                startAdornment={
                  <InputAdornment position="start">
                    <AttachMoneyOutlinedIcon
                      sx={{ color: "rgba(0, 0, 0, 0.87)", fontSize: 20 }}
                    />
                  </InputAdornment>
                }
                value={formik.values.inspectorFee}
                onChange={formik.handleChange}
              />
              {Boolean(formik.errors.inspectorFee) &&
                formik.touched.inspectorFee && (
                  <FormHelperText>{formik.errors.inspectorFee}</FormHelperText>
                )}
            </FormControl>
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Box
            sx={{
              width: 192,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: 12, color: "rgba(0, 0, 0, 0.6)" }}>
              Photo Resolution
            </Typography>
            <Tooltip
              title="Original resolution should only be selected if a form absolutely requires full size images as a final output and may incur additional costs."
              arrow
              placement="top"
            >
              <IconButton sx={{ p: 0, mr: 2 }}>
                <InfoOutlinedIcon color="primary" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <RadioGroup>
              <FormControlLabel
                control={
                  <Radio
                    name="photoResolution"
                    checked={
                      formik.values.photoResolution ===
                      photoResolutions.standardResolution
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        formik.setFieldValue(
                          "photoResolution",
                          photoResolutions.standardResolution
                        );
                      }
                    }}
                  />
                }
                label={
                  <Typography display={"flex"} alignItems={"center"}>
                    Standard Resolution
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontStyle: "italic",
                        color: "rgba(0, 0, 0, 0.6)",
                        ml: 0.5,
                      }}
                    >
                      (Default)
                    </Typography>
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Radio
                    name="photoResolution"
                    checked={
                      formik.values.photoResolution ===
                      photoResolutions.originalResolution
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        formik.setFieldValue(
                          "photoResolution",
                          photoResolutions.originalResolution
                        );
                      }
                    }}
                  />
                }
                label="Original Resolution"
              />
            </RadioGroup>
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Restrictions</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <Box>
              {restrictions?.length === 0 && (
                <Typography>No Restrictions</Typography>
              )}
              {restrictions?.map((restriction, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mt: index === 0 ? 0 : 2,
                    position: "relative",
                  }}
                >
                  <FormControl
                    sx={{ flex: 1 }}
                    error={!restriction.type && formik.submitCount > 0}
                  >
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={restriction.type}
                      onChange={(e) => onRestrictionTypeChange(e, index)}
                    >
                      {formTemplateRestrictionOptions.map((option) => (
                        <MenuItem
                          key={option}
                          value={option}
                          disabled={checkDuplicate(option)}
                        >
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                    {!restriction.type && formik.submitCount > 0 && (
                      <Typography
                        sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                        color="error"
                      >
                        This field can't be empty
                      </Typography>
                    )}
                  </FormControl>
                  {restriction.type === "Background Check Completed" && (
                    <>
                      <FormControl sx={{ flex: 2, ml: 2, mr: 6 }}>
                        <Select
                          labelId="demo-simple-select-label"
                          id="demo-simple-select"
                          value={backgroundCheck}
                          onChange={onBackgroundCheckChange}
                        >
                          <MenuItem value={"true"}>True</MenuItem>
                          <MenuItem value={"false"}>False</MenuItem>
                        </Select>
                        {!backgroundCheck && formik.submitCount > 0 && (
                          <Typography
                            sx={{ fontSize: 12, ml: 2, mt: 0.5 }}
                            color="error"
                          >
                            This field can't be empty
                          </Typography>
                        )}
                      </FormControl>
                    </>
                  )}

                  {restriction.type === "Insurance Required" && (
                    <>
                      <FormControl
                        sx={{ flex: 2, ml: 2, mr: 6 }}
                        error={
                          insurances &&
                          insurances.length === 0 &&
                          formik.submitCount > 0
                        }
                      >
                        <Select
                          labelId="demo-multiple-chip-label"
                          id="demo-multiple-chip"
                          multiple
                          value={insurances}
                          onChange={onInsuranceChange}
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {selected.map((value) => (
                                <Chip
                                  color="primary"
                                  variant="outlined"
                                  onDelete={() => onInsuranceDelete(value)}
                                  key={value}
                                  label={getInsuranceDisplayName(value)}
                                  deleteIcon={
                                    <IconButton
                                      onMouseDown={(e) => e.stopPropagation()}
                                      size="small"
                                      sx={{ padding: 0.5 }}
                                    >
                                      <CancelIcon fontSize="small" />
                                    </IconButton>
                                  }
                                />
                              ))}
                            </Box>
                          )}
                        >
                          {insuranceTypes.map((option: any) => (
                            <MenuItem
                              key={option.insuranceType}
                              value={option.insuranceType}
                            >
                              {option.displayName}
                            </MenuItem>
                          ))}
                        </Select>
                        {insurances &&
                          insurances.length === 0 &&
                          formik.submitCount > 0 && (
                            <FormHelperText>
                              This field can't be empty
                            </FormHelperText>
                          )}
                      </FormControl>
                    </>
                  )}

                  {restriction.type === "Certifications" && (
                    <>
                      <FormControl
                        sx={{ flex: 2, ml: 2, mr: 6 }}
                        error={
                          certifications &&
                          certifications.length === 0 &&
                          formik.submitCount > 0
                        }
                      >
                        <Select
                          labelId="demo-multiple-chip-label"
                          id="demo-multiple-chip"
                          multiple
                          value={certifications}
                          onChange={onCertificationChange}
                          renderValue={(selected) => (
                            <Box
                              sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.5,
                              }}
                            >
                              {selected.map((value) => (
                                <Chip
                                  color="primary"
                                  variant="outlined"
                                  onDelete={() => onCertificationDelete(value)}
                                  key={value}
                                  label={getCertificationDisplayName(value)}
                                  deleteIcon={
                                    <IconButton
                                      onMouseDown={(e) => e.stopPropagation()}
                                      size="small"
                                      sx={{ padding: 0.5 }}
                                    >
                                      <CancelIcon fontSize="small" />
                                    </IconButton>
                                  }
                                />
                              ))}
                            </Box>
                          )}
                        >
                          {certificationTypes.map((option: any) => (
                            <MenuItem
                              key={option.certificationType}
                              value={option.certificationType}
                            >
                              {option.displayName}
                            </MenuItem>
                          ))}
                        </Select>
                        {certifications &&
                          certifications.length === 0 &&
                          formik.submitCount > 0 && (
                            <FormHelperText>
                              This field can't be empty
                            </FormHelperText>
                          )}
                      </FormControl>
                    </>
                  )}

                  {restriction.type === "" && (
                    <Box sx={{ flex: 2, marginRight: 8 }}></Box>
                  )}

                  <IconButton
                    sx={{ position: "absolute", right: 0 }}
                    aria-label="delete"
                    onClick={() => deleteRestictrionClick(index)}
                  >
                    <DeleteOutlineOutlinedIcon style={{ color: "#D32F2F" }} />
                  </IconButton>
                </Box>
              ))}

              {restrictions?.length < 3 && (
                <Box mt={2}>
                  <Button
                    variant="text"
                    startIcon={<AddIcon />}
                    onClick={addRestictrionClick}
                  >
                    ADD RESTRICTION
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Always Require 2nd QC</Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    name="secondQC"
                    checked={formik.values.secondQC}
                    onChange={formik.handleChange}
                  />
                }
                label={formik.values.secondQC ? "Yes" : "No"}
              />
            </FormGroup>
          </Box>
        </Box>
      </form>
      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
      {(showLoading || loading || formLoading) && <Loading />}
    </DialogContainer>
  );
}
